import { createContext, useContext, useEffect, useRef, useState } from "react";
import { NavLink, type NavLinkProps, type NavLinkRenderProps } from "react-router";
import { useLocalStorage } from "usehooks-ts";

import { cn } from "../utils";
import { Badge } from "./badge";
import {
  Collapsible,
  CollapsibleContent,
  type CollapsibleContentProps,
  type CollapsibleProps,
  CollapsibleTrigger,
  type CollapsibleTriggerProps,
} from "./collapsible";
import { Icon, type MaterialSymbol } from "./icon";

const CollapsibleListOpenContext = createContext(false);

export type CollapsibleListProps = CollapsibleProps & {
  /**
   * Holds the list open while true and lets it fall back to its saved state once false, for a current route that
   * no rendered link marks as current. Leave it unset to latch on a current link at mount instead.
   */
  current?: boolean;
};

export const CollapsibleList = ({
  id,
  className,
  current: currentRoute,
  defaultOpen = false,
  ...props
}: CollapsibleListProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [current, setCurrent] = useState(false);
  const [expanded, setExpanded] = useLocalStorage(`qw-sidebar-${id}-expanded`, defaultOpen);

  useEffect(() => {
    if (ref.current?.querySelector("a[aria-current]")) {
      setCurrent(true);
    }
  }, [ref]);

  useEffect(() => {
    if (currentRoute !== undefined) {
      setCurrent(currentRoute);
    }
  }, [currentRoute]);

  const open = current || expanded;

  return (
    <CollapsibleListOpenContext.Provider value={open}>
      <Collapsible
        ref={ref}
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setCurrent(false);
          }
          setExpanded(nextOpen);
        }}
        className={cn("border-b transition-colors", className)}
        {...props}
      />
    </CollapsibleListOpenContext.Provider>
  );
};

export type CollapsibleListTriggerProps = CollapsibleTriggerProps & { count?: number };

export const CollapsibleListTrigger = ({ children, className, count, ...props }: CollapsibleListTriggerProps) => (
  <CollapsibleTrigger
    className={cn(
      "group cursor-pointer flex font-semibold gap-3.5 h-11 items-center px-6 relative text-left text-muted-foreground hover:bg-foreground/2.5 data-[state=open]:text-foreground transition-colors w-full",
      className
    )}
    {...props}
  >
    <Icon
      icon="keyboard_arrow_right"
      className="flex rounded size-4 text-muted-foreground text-lg transition-transform group-data-[state=open]:rotate-90"
    />
    <span className="flex-auto text-xs truncate">{children}</span>
    {count && (
      <Badge variant="secondary" type="square" size="2xs" className="bg-foreground/5">
        {count}
      </Badge>
    )}
  </CollapsibleTrigger>
);

export interface CollapsibleListHeaderProps {
  icon: MaterialSymbol;
  count?: number;
  children: string;
  className?: string;
  /** Makes the icon and label a link to the full list, active only on that exact route. */
  to?: NavLinkProps["to"];
  onClick?: () => void;
  /** Every item is history: the count is muted, and a spacer holds the chevron's place since there are no rows. */
  inactive?: boolean;
}

// The label fills the row up to the chevron, so the whole row is one target.
const headerLabelClassName = "flex flex-auto gap-4 items-center min-w-0 pl-6 self-stretch text-xs";

export const CollapsibleListHeader = ({
  children,
  className,
  count,
  icon,
  to,
  onClick,
  inactive = false,
}: CollapsibleListHeaderProps) => {
  const open = useContext(CollapsibleListOpenContext);
  const label = (
    <>
      <Icon icon={icon} className="flex flex-none items-center justify-center size-4 text-base" />
      <span className="truncate">{children}</span>
      {!!count && (
        <Badge
          variant="secondary"
          type="square"
          size="2xs"
          className={cn("bg-foreground/5 ml-auto", inactive && "font-medium text-muted-foreground")}
        >
          {count}
        </Badge>
      )}
    </>
  );

  return (
    <div
      data-state={open ? "open" : "closed"}
      className={cn(
        "group flex font-semibold gap-2 h-11 items-center pr-6 text-muted-foreground data-[state=open]:text-foreground",
        to && "hover:bg-foreground/2.5 transition-colors",
        inactive && "text-foreground",
        className
      )}
    >
      {to ? (
        <NavLink
          end
          to={to}
          onClick={onClick}
          className={({ isActive }: NavLinkRenderProps) =>
            cn(headerLabelClassName, "hover:text-foreground transition-colors", isActive && "filled text-foreground")
          }
        >
          {label}
        </NavLink>
      ) : (
        <span className={headerLabelClassName}>{label}</span>
      )}
      {inactive ? (
        <span aria-hidden className="-mr-1.5 flex-none size-6" />
      ) : (
        <CollapsibleTrigger
          aria-label={`${open ? "Collapse" : "Expand"} ${children}`}
          className="-mr-1.5 cursor-pointer flex flex-none items-center justify-center rounded size-6 hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
        >
          <Icon
            icon="keyboard_arrow_right"
            className="text-lg text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
          />
        </CollapsibleTrigger>
      )}
    </div>
  );
};

export const CollapsibleListContent = ({ className, ...props }: CollapsibleContentProps) => (
  <CollapsibleContent forceMount className={cn("hidden data-[state=open]:block", className)} {...props} />
);

export type CollapsibleListLinkProps = NavLinkProps & {
  icon?: React.ReactNode;
  header: React.ReactNode;
  subheader?: React.ReactNode;
};

export const CollapsibleListLink = ({
  children,
  className,
  icon,
  header,
  subheader,
  ...props
}: CollapsibleListLinkProps) => (
  <div className={cn("group relative", className)}>
    {icon && (
      <span className="absolute flex items-center justify-center left-6 pointer-events-none size-4 text-sm top-3">
        {icon}
      </span>
    )}
    <NavLink
      className={({ isActive }: NavLinkRenderProps) =>
        cn(
          "group hover:bg-foreground/2.5 flex flex-col min-h-11 pl-14 pr-6 py-2.5 space-y-1 text-muted-foreground hover:text-foreground transition-colors",
          isActive && "is-active bg-foreground/5! text-foreground"
        )
      }
      {...props}
    >
      <div className="group-[.is-active]:text-foreground! group-hover:text-foreground! line-clamp-3 text-xs text-foreground/75">
        {header}
      </div>
      {subheader && <div className="text-2xs text-muted-foreground truncate">{subheader}</div>}
    </NavLink>
  </div>
);

export const CollapsibleListGroup = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("p-0", className)} {...props} />
);
